/**
 * Write a number the way the *tenant* groups numbers.
 *
 * An amount was written with a locale spelled out in the source —
 * `number(amount)` — in 74 places. 'en-IN' groups as
 * 1,00,000, which is right for today's tenants and wrong as a decision taken
 * by the code: a tenant that counts in 100,000 had no way to say so.
 *
 * WHO DECIDES
 *   The tenant, through the DISPLAY_NUMBER_FORMAT system parameter (m01_046),
 *   set by a tenant administrator on System Configuration and delivered to
 *   every screen with the tenant identity.
 *
 * WHY THE VALUE IS A LOCALE TAG AND NOT A MASK
 *   Grouping is not a pattern worth hand-writing. Indian grouping puts the
 *   first separator after three digits and every later one after two;
 *   German swaps the roles of '.' and ','. `Intl.NumberFormat` already knows
 *   all of it. Storing a tag keeps Intl the authority; storing '#,##,##0.00'
 *   would mean re-implementing it here, badly, in a file that must also run
 *   under Metro.
 *
 * NOT THE LANGUAGE PICKER
 *   A reader may read the product in Hindi while the tenant still counts in
 *   Indian grouping. Tying the two together is the defect being repaired, so
 *   nothing here consults the reader's locale.
 *
 * MONEY IS NOT A SPECIAL CASE OF NUMBER
 *   `formatMoney` exists separately because an amount of money always shows
 *   its paise — 1,000 and 1,000.00 are the same quantity but only the second
 *   reads as a sum of money, and a column of mixed shapes cannot be scanned.
 *   The currency symbol is NOT added here: M00 Convention 6 fixes the launch
 *   currency as INR and every screen already writes its own ₹, so adding one
 *   would double it.
 */

/** Fallback when no tenant setting has been resolved. Matches m01_046. */
export const DEFAULT_NUMBER_FORMAT = 'en-IN';

/**
 * The formats a tenant may choose, and the only ones a screen will honour.
 *
 * This list and the `allowed_enum` of the DISPLAY_NUMBER_FORMAT catalogue row
 * are the same set stated in two languages, so a test asserts they agree — an
 * administrator must never be offered a format the screens ignore.
 */
export const NUMBER_FORMATS = ['en-IN', 'en-US', 'en-GB', 'de-DE'] as const;

export type NumberFormat = (typeof NUMBER_FORMATS)[number];

/** Type-guard: is `value` a grouping these screens will honour. */
export function isNumberFormat(value: unknown): value is NumberFormat {
  return (
    typeof value === 'string' &&
    (NUMBER_FORMATS as readonly string[]).includes(value)
  );
}

function resolve(format: string): string {
  return isNumberFormat(format) ? format : DEFAULT_NUMBER_FORMAT;
}

/**
 * A quantity — a count, a size, a percentage's number — grouped the tenant's
 * way. Decimals are shown only when the value has them, because a count of
 * boards reads as 12, not 12.00.
 *
 * `fractionDigits` fixes the number of decimals for a figure whose screen has
 * always shown a set precision — a score to one place, a relevance to three —
 * so moving it onto the tenant's grouping does not also change what it says.
 *
 * A value that is not a finite number returns an em dash rather than NaN,
 * which is what an empty cell should look like.
 */
export function formatNumber(
  value: number | null | undefined,
  format: string,
  fractionDigits?: number,
): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return '\u2014';
  }
  return new Intl.NumberFormat(
    resolve(format),
    fractionDigits === undefined
      ? { maximumFractionDigits: 2 }
      : { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits },
  ).format(value);
}

/**
 * An amount of money, grouped the tenant's way and always carrying its paise.
 *
 * No currency symbol: the caller supplies ₹, as every screen already does.
 */
export function formatMoney(
  value: number | null | undefined,
  format: string,
): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return '\u2014';
  }
  return new Intl.NumberFormat(resolve(format), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

const PLAIN_DECIMAL = /^(-)?(\d+)(?:\.(\d+))?$/;

/**
 * A stored decimal — a quantity, a dimension, a rate, a percentage — written
 * exactly as it was recorded, grouped the tenant's way.
 *
 * `formatNumber` rounds to two places, which is right for a figure the screen
 * computed and wrong for one the database holds: t620 keeps quantities to four
 * places and dimensions to three, and rounding them on screen shows a value
 * that was never agreed. So the stored digits are kept, padding zeros are
 * dropped (10.0000 reads as 10), and only the grouping and the decimal mark
 * follow the tenant.
 *
 * The digits are never passed through a floating-point number: the integer
 * part is grouped by Intl only while it is a safe integer, and the fraction is
 * appended as written. A value too large to group exactly is returned as
 * written rather than grouped wrongly, and text that is not a plain decimal is
 * returned untouched so malformed data shows itself.
 */
export function formatDecimal(
  value: string | number | null | undefined,
  format: string,
): string {
  if (value === null || value === undefined) return '\u2014';
  if (typeof value === 'number' && !Number.isFinite(value)) return '\u2014';
  const text = String(value).trim();
  if (text === '') return '\u2014';
  const match = PLAIN_DECIMAL.exec(text);
  if (!match) return text;
  const [, sign = '', whole, fraction = ''] = match;
  const integer = Number(whole);
  if (!Number.isSafeInteger(integer)) return text;
  const locale = resolve(format);
  const digits = fraction.replace(/0+$/, '');
  const grouped = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(integer);
  const mark = new Intl.NumberFormat(locale).format(1.5).replace(/\d/g, '');
  const negative = sign && (integer !== 0 || digits !== '') ? '-' : '';
  return `${negative}${grouped}${digits ? `${mark}${digits}` : ''}`;
}
